import React, { Component } from 'react';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import { graphql } from 'react-apollo';
import gql from 'graphql-tag';
import { compose, defaultProps } from 'recompose';
import { propType } from 'graphql-anywhere';
import DropdownList from 'react-widgets/lib/DropdownList';
import {
  Button,
  Glyphicon,
  InputGroup,
} from 'react-bootstrap';

import { querySelector } from '../../utils';
import createOption from '../../utils/dropdownListCreateOption';
import createAdapter from '../../utils/fuseFilterAdapter';
import { OperatorInputOperatorFragment } from './OperatorInput';
import LookupResultListItem from '../../features/lookups/components/LookupResultListItem';
import CompatLookupResultListItem from '../../features/lookups/components/CompatLookupResultListItem';

export class LeaseInput extends Component {
  constructor(props) {
    super(props);
    this.state = {
      open: false,
    };
    this.handleOnFocus = this.handleOnFocus.bind(this);
    this.handleOnToggle = this.handleOnToggle.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleCreate = this.handleCreate.bind(this);
    this.maybeCreateFromLookup = this.maybeCreateFromLookup.bind(this);
  }

  async handleCreate(name) {
    const operatorId = this.props.operator.user_id;
    const variables = {
      input: {
        name,
        operator_id: operatorId,
      },
    };
    try {
      const result = await this.props.createLease({ variables });
      await this.props.data.refetch();
      const lease = result.data.lease_create;
      this.handleChange(lease);
    } catch (e) {
      console.error('error', e);
    }
  }

  async handleCreateFromLookup(lookupId) {
    const variables = {
      input: {
        lease_lookup_id: lookupId,
      },
    };

    const result = await this.props.createLeaseFromLookup({ variables });
    await this.props.data.refetch();
    return result.data.lookup_result;
  }

  async maybeCreateFromLookup(maybeLookup) {
    if (!maybeLookup) {
      return null;
    }

    if (maybeLookup.lookup_status === 'reference') {
      const newRecord = await this.handleCreateFromLookup(maybeLookup.lookup_id);
      return newRecord;
    }

    return maybeLookup;
  }

  async handleChange(data) {
    const record = await this.maybeCreateFromLookup(data);

    return this.props.onChange({
      target: {
        name: this.props.name,
        value: record,
      },
    });
  }

  handleCancel() {
    this.handleChange(null);
  }

  handleOnFocus() {
    this.setState({
      open: true,
    });
  }

  handleOnToggle(open) {
    this.setState({
      open,
    });
  }

  render() {
    const { leases, value, disabled, lookupJoinType } = this.props;
    const { open } = this.state;
    const filter = createAdapter(leases);
    const itemComponent = lookupJoinType === 'union' ? LookupResultListItem : CompatLookupResultListItem;

    return (
      <InputGroup>
        {
          /*
            The on mouse down prevents the 'onClick' from firing. This was toggling the dropdown
            twice. If you have a better way of prevent the double toggle by all means implement it.
          */
        }
        <DropdownList
          disabled={disabled}
          inputProps={{
            className: 'form-control',
          }}
          filter={filter}
          allowCreate="onFilter"
          value={value}
          data={leases}
          open={open}
          onFocus={this.handleOnFocus}
          onToggle={this.handleOnToggle}
          onCreate={this.handleCreate}
          onChange={this.handleChange}
          onMouseDown={(e) => { e.preventDefault(); }}
          valueField="id"
          textField="name"
          messages={{ createOption }}
          itemComponent={itemComponent}
        />
        <InputGroup.Button>
          <Button
            name="cancel"
            tabIndex="-1"
            disabled={!value || disabled}
            onClick={this.handleCancel}
          >
            <Glyphicon glyph="remove" />
          </Button>
        </InputGroup.Button>
      </InputGroup>
    );
  }
}

export const LeaseInputLeaseFragment = gql`
  fragment LeaseInputLeaseLegacy on LeaseLookupResult {
    id
    user_id
    lookup_id
    name
    lookup_status
    lookupStatus: lookup_status
  }
`;

const GetAllLeasesQuery = gql`
  query GetAllLeases($operatorId: Int, $lookupJoinType: LookupJoinTypes!) {
    all_leases: lookup_leases(operator_id: $operatorId, lookup_query: {join_type: $lookupJoinType}) {
      edges {
        node {
          ...LeaseInputLeaseLegacy
        }
      }
    }
  }
  ${LeaseInputLeaseFragment}
`;

const CreateLeaseMutation = gql`
  mutation CreateLeaseMutation($input: LeaseCreateFromNameInput!) {
    lease_create: lease_create_from_name(input: $input) {
      ...LeaseInputLeaseLegacy
    }
  }
  ${LeaseInputLeaseFragment}
`;

const CreateLeaseFromLookupMutation = gql`
  mutation LeaseCreateFromLookup($input: LeaseCreateFromLookupInput!) {
    lookup_result: lease_create_from_lookup(input: $input) {
      ...LeaseInputLeaseLegacy
    }
  }
  ${LeaseInputLeaseFragment}
`;

export const marshallData = ({ data, ownProps }) => {
  const leases = querySelector(data, '$.all_leases.edges[*].node', []);
  return {
    ...ownProps,
    data,
    leases,
  };
};

const queryVariables = (props) => {
  const operatorId = (props.operator) && props.operator.user_id;
  const { lookupJoinType } = props;

  return {
    fetchPolicy: 'network-only',
    variables: {
      operatorId,
      lookupJoinType,
    },
  };
};

const withData = graphql(GetAllLeasesQuery, {
  props: marshallData,
  options: (props) => {
    const qv = queryVariables(props);
    return {
      variables: qv.variables,
      fetchPolicy: qv.fetchPolicy,
      skip: !props.operator || !props.operator.user_id,
    };
  },
});

const withCreate = graphql(CreateLeaseMutation, { name: 'createLease' });

const withCreateFromLookup = graphql(CreateLeaseFromLookupMutation, { name: 'createLeaseFromLookup' });

const enhance = compose(
  defaultProps({ lookupJoinType: 'user_records' }),
  withData,
  withCreate,
  withCreateFromLookup,
);

const propTypes = exact({
  name: PropTypes.string.isRequired,
  value: propType(LeaseInputLeaseFragment),
  onChange: PropTypes.func.isRequired,
  operator: propType(OperatorInputOperatorFragment),
  disabled: PropTypes.bool,
  data: PropTypes.object,
  leases: PropTypes.arrayOf(propType(LeaseInputLeaseFragment)),
  // eslint-disable-next-line react/no-unused-prop-types
  lookupJoinType: PropTypes.oneOf(['user_records', 'lookup_records', 'union']),
  createLease: PropTypes.func,
  createLeaseFromLookup: PropTypes.func,
});

LeaseInput.propTypes = propTypes;

export default enhance(LeaseInput);
